import { Camera, FolderClock } from "lucide-react";

import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { formatSmileSimulationDateTime } from "../lib/session";
import { getSmileSimulationCopy } from "../model/i18n";
import type { SmileSimulationLanguage } from "../model/types";
import { ExperienceSimulationLayout } from "./ExperienceSimulationLayout";
import { SmileSimulationEmptyState } from "./SmileSimulationEmptyState";

type SmileSimulationHomePageProps = {
  clinic: SmileSimulationController["clinic"];
  home: SmileSimulationController["home"];
  isExperience?: boolean;
  language: SmileSimulationLanguage;
  onOpenPatient: SmileSimulationController["actions"]["openPatientDetail"];
  onOpenHistory: SmileSimulationController["actions"]["openHistory"];
  onStartNewSimulation: SmileSimulationController["actions"]["startNewSimulation"];
};

export function SmileSimulationHomePage({
  clinic,
  home,
  isExperience = false,
  language,
  onOpenPatient,
  onOpenHistory,
  onStartNewSimulation,
}: SmileSimulationHomePageProps) {
  const copy = getSmileSimulationCopy(language);

  if (isExperience) {
    return (
      <ExperienceSimulationLayout
        footer={
          <button
            type="button"
            onClick={() => onStartNewSimulation()}
            className="h-14 w-full rounded-[1.25rem] bg-[#8F14E5] px-5 text-center text-lg font-semibold text-white shadow-[0_20px_40px_rgba(143,20,229,0.24)] transition hover:bg-[#7d10ca] sm:h-16 sm:text-xl"
          >
            스마일 시뮬레이션
          </button>
        }
      >
        <div className="relative mx-auto flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#2b2b2b] shadow-[0_20px_50px_rgba(17,17,17,0.1)]">
          <div className="px-8 text-center text-white">
            <p className="text-[1.55rem] font-extrabold leading-snug sm:text-[1.9rem]">
              당신이 몰랐던,
              <br />
              내일의 <span className="text-[#8F14E5]">미소</span>를 만날 시간
            </p>
          </div>
        </div>
      </ExperienceSimulationLayout>
    );
  }

  return (
    <section className="flex h-full min-h-0 w-full flex-1 flex-col gap-4 overflow-y-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-6 [&::-webkit-scrollbar]:hidden">
      <div className="space-y-2 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.16em] text-[#8F14E5]/70">
          Clinic Dashboard
        </p>
        <h2 className="text-2xl font-semibold text-[#1f1230] sm:text-[1.75rem]">
          {isExperience ? "Boost UP! your Clinic!" : copy.home.title}
        </h2>
        {!isExperience ? (
          <p className="text-sm leading-6 text-[#5f3a78]">
            {copy.home.accountLabel}{" "}
            <span className="font-semibold text-[#1f1230]">{clinic.email}</span>
          </p>
        ) : null}
      </div>

      <div className="grid w-full gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => onStartNewSimulation()}
          className="rounded-[1.75rem] bg-[#8F14E5] p-5 text-left text-white shadow-[0_20px_40px_rgba(143,20,229,0.24)] transition hover:bg-[#7d10ca]"
        >
          <Camera className="h-6 w-6" />
          <p className="mt-5 text-lg font-semibold">
            {copy.home.newSimulation}
          </p>
        </button>

        <button
          type="button"
          onClick={onOpenHistory}
          className="rounded-[1.75rem] border border-white/70 bg-white/90 p-5 text-left text-[#1f1230] shadow-[0_18px_45px_rgba(17,17,17,0.08)] backdrop-blur-sm transition hover:bg-white"
        >
          <FolderClock className="h-6 w-6 text-[#8F14E5]" />
          <p className="mt-5 text-lg font-semibold">
            {isExperience ? "참가자 기록 보기" : copy.home.history}
          </p>
        </button>
      </div>

      <div className="grid w-full gap-3 sm:grid-cols-2">
        <div className="bg-white/88 rounded-[1.5rem] border border-white/70 p-4 shadow-[0_16px_40px_rgba(17,17,17,0.06)] backdrop-blur-sm">
          <p className="text-sm font-medium text-[#6b577d]">
            {copy.home.todayCount}
          </p>
          <p className="mt-2 text-3xl font-semibold text-[#1f1230]">
            {home.todayRecordCount}
          </p>
        </div>
        <div className="bg-white/88 rounded-[1.5rem] border border-white/70 p-4 shadow-[0_16px_40px_rgba(17,17,17,0.06)] backdrop-blur-sm">
          <p className="text-sm font-medium text-[#6b577d]">
            {isExperience ? "전체 참가자 기록" : copy.home.totalCount}
          </p>
          <p className="mt-2 text-3xl font-semibold text-[#1f1230]">
            {home.recordCount}
          </p>
        </div>
      </div>

      <div className="w-full">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-[#1f1230]">
            {isExperience ? "최근 작업 참가자" : copy.home.recentPatients}
          </h3>
          {home.patientCount > 0 ? (
            <button
              type="button"
              onClick={onOpenHistory}
              className="text-sm font-semibold text-[#8F14E5]"
            >
              {copy.home.viewAll}
            </button>
          ) : null}
        </div>

        {home.recentPatients.length > 0 ? (
          <div className="grid gap-3">
            {home.recentPatients.map((patient) => (
              <button
                key={patient.id}
                type="button"
                onClick={() => onOpenPatient(patient.id)}
                className="bg-white/88 rounded-[1.5rem] border border-white/70 p-4 text-left shadow-[0_16px_40px_rgba(17,17,17,0.06)] backdrop-blur-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    {isExperience ? (
                      <p className="text-base font-semibold text-[#1f1230]">
                        {patient.chartNumber}
                      </p>
                    ) : (
                      <>
                        <p className="text-base font-semibold text-[#1f1230]">
                          {patient.name}
                        </p>
                        <p className="text-sm text-[#6b577d]">
                          Chart No. {patient.chartNumber}
                        </p>
                      </>
                    )}
                  </div>
                  {!isExperience ? (
                    <div className="rounded-full bg-[#f3e8ff] px-3 py-1 text-xs font-semibold text-[#7c3aed]">
                      {patient.recordCount}
                      {language === "ko" ? "" : " "}
                      {copy.home.recordUnit}
                    </div>
                  ) : null}
                </div>
                {patient.latestRecord ? (
                  <p className="mt-3 text-sm text-[#6b577d]">
                    {copy.home.latestCreated}{" "}
                    {formatSmileSimulationDateTime(
                      patient.latestRecord.createdAt,
                    )}
                  </p>
                ) : null}
              </button>
            ))}
          </div>
        ) : (
          <SmileSimulationEmptyState
            title={copy.home.emptyTitle}
            description={
              isExperience
                ? "새 스마일 시뮬레이션을 시작하면 참가자 기록이 여기에 쌓입니다."
                : copy.home.emptyDescription
            }
            actionLabel={copy.home.emptyAction}
            onAction={() => onStartNewSimulation()}
          />
        )}
      </div>
    </section>
  );
}
